import { useCallback } from "react";
import { trackEvent } from "@/lib/analytics/tracking";
import { useAppAuth } from "@/auth/use-app-auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Axios } from "@/lib/axios";

export interface Plan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  price: string;
  priceAnnual?: string | null;
  billingPeriod: string;
  isActive: boolean;
  jobPostLimit: number | null;
  resumeLimit: number;
  interviewLimit: number;
  searchLimit: number | null;
  features: Record<string, boolean>;
  stripePriceId: string | null;
  stripeProductId: string | null;
}

export interface Subscription {
  id: string;
  planId: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialStart?: string | null;
  trialEnd?: string | null;
  plan: Plan;
}

// Query keys for consistent caching
const QUERY_KEYS = {
  plans: ["billing", "plans"] as const,
  subscription: ["billing", "subscription"] as const,
};

export const useBilling = () => {
  const { isAuthenticated, user } = useAppAuth();
  const queryClient = useQueryClient();

  // Fetch plans query - shared across all components
  const {
    data: plans = [],
    isLoading: plansLoading,
    error: plansError,
  } = useQuery({
    queryKey: QUERY_KEYS.plans,
    queryFn: async (): Promise<Plan[]> => {
      const response = await Axios.get("/api/plans");
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - plans don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
  });

  // Fetch subscription query - only when authenticated
  const {
    data: subscription = null,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = useQuery({
    queryKey: QUERY_KEYS.subscription,
    queryFn: async (): Promise<Subscription | null> => {
      try {
        const response = await Axios.get("/api/billing/subscription/current");
        return response.data;
      } catch (error: any) {
        // If user has no subscription, return null instead of throwing
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: isAuthenticated, // Only run when user is authenticated
    staleTime: 2 * 60 * 1000, // 2 minutes - subscription can change more frequently
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
  });

  // Upgrade subscription mutation
  const upgradeSubscriptionMutation = useMutation({
    mutationFn: async ({
      planId,
      billingPeriod,
      couponCode,
    }: {
      planId: string;
      billingPeriod?: "MONTHLY" | "ANNUAL";
      couponCode?: string;
    }) => {
      const response = await Axios.post("/api/billing/checkout/create", {
        planId,
        billingPeriod: billingPeriod || "MONTHLY",
        couponCode,
        customerEmail: user?.email,
        successUrl: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/pricing`,
      });
      return response.data;
    },
    onSuccess: (data, { planId }) => {
      trackEvent("checkout_started", { item_type: "plan", item_id: planId });
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error("Failed to create checkout session:", error);
      throw new Error("Failed to start upgrade process");
    },
  });

  // Purchase feature pack mutation
  const purchasePackMutation = useMutation({
    mutationFn: async (packId: string) => {
      const response = await Axios.post("/api/billing/checkout/pack", {
        packId,
        customerEmail: user?.email,
        successUrl: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/pricing`,
      });
      return response.data;
    },
    onSuccess: (data, packId) => {
      trackEvent("checkout_started", { item_type: "pack", item_id: packId });
      // Redirect to Stripe Checkout for one-time payment
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error("Failed to create pack checkout session:", error);
      throw new Error("Failed to start pack purchase");
    },
  });

  // Open customer portal mutation
  const openPortalMutation = useMutation({
    mutationFn: async () => {
      const response = await Axios.post("/api/billing/portal", {});
      return response.data;
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error("Failed to open customer portal:", error);
      throw new Error("Failed to open customer portal");
    },
  });

  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      await Axios.delete("/api/billing/subscription/cancel");
    },
    onSuccess: () => {
      // Invalidate and refetch subscription data
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription });
    },
    onError: (error) => {
      console.error("Failed to cancel subscription:", error);
      throw new Error("Failed to cancel subscription");
    },
  });

  // Wrapper functions to maintain the same API
  const upgradeSubscription = useCallback(
    async (
      planId: string,
      billingPeriod?: "MONTHLY" | "ANNUAL",
      couponCode?: string
    ) => {
      return upgradeSubscriptionMutation.mutateAsync({ planId, billingPeriod, couponCode });
    },
    [upgradeSubscriptionMutation]
  );

  const purchasePack = useCallback(
    async (packId: string) => {
      return purchasePackMutation.mutateAsync(packId);
    },
    [purchasePackMutation]
  );

  const openCustomerPortal = useCallback(async () => {
    return openPortalMutation.mutateAsync();
  }, [openPortalMutation]);

  const cancelSubscription = useCallback(async () => {
    return cancelSubscriptionMutation.mutateAsync();
  }, [cancelSubscriptionMutation]);

  // Manual refetch functions
  const refetchSubscription = useCallback(async () => {
    return queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription });
  }, [queryClient]);

  const refetchPlans = useCallback(async () => {
    return queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
  }, [queryClient]);

  // Compute overall loading and error states
  const loading = plansLoading || (isAuthenticated && subscriptionLoading);
  const error = plansError || subscriptionError;

  return {
    subscription,
    plans,
    loading,
    error: error ? (error as Error).message : null,
    upgradeSubscription,
    purchasePack,
    openCustomerPortal,
    cancelSubscription,
    refetchSubscription,
    refetchPlans,
    // Additional React Query states if needed
    isUpgrading: upgradeSubscriptionMutation.isPending,
    isPurchasingPack: purchasePackMutation.isPending,
    isCanceling: cancelSubscriptionMutation.isPending,
    isOpeningPortal: openPortalMutation.isPending,
  };
};
